import type en from "../en/account";
import type { Messages } from "../../types";

const fr: Messages<typeof en> = {
  // Stores list
  storesTitle: "Vos boutiques",
  storesDescription: "Choisissez une boutique à gérer ou créez-en une nouvelle.",
  createStore: "Créer une boutique",
  upgradeToAddStores: "Passer à un forfait supérieur pour ajouter des boutiques",
  verifyEmailNotice: "Veuillez vérifier votre adresse e-mail — nous avons envoyé un lien à {email}.",
  resend: "Renvoyer",
  firstStoreTitle: "Créez votre première boutique",
  firstStoreDescription: "Cela ne prend que quelques minutes. Votre boutique peut rester privée jusqu’à ce que vous soyez prêt à la publier.",
  createAStore: "Créer une boutique",
  currentPlan: "Forfait actuel",
  managePlan: "Gérer le forfait",

  // New store
  newStoreMeta: "Créer une boutique",
  welcomeTitle: "Bienvenue ! Configurons votre boutique",
  newStoreTitle: "Créer une nouvelle boutique",
  newStoreDescription: "Étape 1 de la configuration — l’essentiel. Vous ajouterez ensuite vos produits, choisirez comment être payé et publierez quand vous serez prêt.",
  storeLimitTitle: "Limite de boutiques atteinte",
  storeLimitBody: "Votre forfait {plan} permet {count} boutiques.",
  storeLimitBody_one: "Votre forfait {plan} permet {count} boutique.",
  seePlans: "Voir les forfaits",
  platformSuffixPath: " (sur {url}/s/…)",
  biz_general: "Commerce généraliste",
  biz_fashion: "Mode et vêtements",
  biz_electronics: "Électronique",
  biz_beauty: "Beauté et cosmétiques",
  biz_home: "Maison et ameublement",
  biz_food: "Alimentation et épicerie fine",
  biz_digital: "Produits numériques",
  biz_services: "Services",
  fieldStoreName: "Nom de la boutique",
  changeLaterHint: "Vous pourrez le modifier plus tard.",
  fieldStoreAddress: "Adresse de la boutique",
  slugTaken: "Cette adresse est déjà prise ou réservée.",
  slugHint: "Votre adresse gratuite : {address}. Vous pourrez connecter votre propre domaine plus tard.",
  slugPlaceholder: "votre-boutique",
  fieldBusinessType: "Qu’allez-vous vendre ?",
  fieldStoreLanguage: "Langue de la boutique",
  fieldCountry: "Pays",
  fieldStoreCurrency: "Devise de la boutique",
  currencyHint: "Les prix et les commandes utilisent cette devise. Elle ne peut plus être modifiée après votre première commande.",
  fieldTimezone: "Fuseau horaire",
  fieldTheme: "Thème de départ",
  themeHint: "Laissez « Recommandé » pour un thème adapté à votre activité.",
  themeRecommended: "Recommandé",
  themePaidPlans: " (forfaits payants)",
  creatingStore: "Création de votre boutique…",

  // Account & security
  accountDescription: "Gérez votre profil, la sécurité de connexion et les notifications.",
  profile: "Profil",
  emailAddress: "Adresse e-mail",
  verified: "Vérifiée",
  notVerified: "Non vérifiée",
  verifiedOn: "Vérifiée le {date}.",
  verifyPrompt: "Vérifiez votre adresse pour pouvoir récupérer votre compte et recevoir les avis de facturation. Consultez votre boîte de réception pour trouver le lien envoyé.",
  resendVerification: "Renvoyer l’e-mail de vérification",
  sending: "Envoi…",
  changePassword: "Changer le mot de passe",
  changePasswordDescription: "Changer votre mot de passe vous déconnecte de tous vos autres appareils.",
  activeSessions: "Sessions actives",
  activeSessionsDescription: "Appareils actuellement connectés à votre compte. Déconnectez ceux que vous ne reconnaissez pas, puis changez votre mot de passe.",
  signOutOthersTitle: "Déconnecter toutes les autres sessions ?",
  signOutOthersBody: "Tous les autres appareils devront se reconnecter.",
  signOutOthersConfirm: "Déconnecter les autres",
  signOutAllOthers: "Déconnecter toutes les autres",
  colDevice: "Appareil",
  colLocation: "Emplacement (IP)",
  colSignedIn: "Connexion",
  colLastActive: "Dernière activité",
  thisDevice: "Cet appareil",
  sessionSignOut: "Déconnecter",
  signingOut: "Déconnexion…",
  notifPrefs: "Préférences de notification",
  notifPrefsDescription: "Choisissez les notifications de boutique que vous recevez dans l’application. Les messages de facturation, de sécurité et du support sont toujours envoyés.",
  closeAccount: "Fermer le compte",
  closeAccountDescription: "Demandez à notre équipe de fermer votre compte Storelancer.",
  emailHint: "Contactez le support pour modifier l’adresse e-mail de votre compte.",
  saveProfile: "Enregistrer le profil",
  currentPassword: "Mot de passe actuel",
  newPassword: "Nouveau mot de passe",
  confirmNewPassword: "Confirmer le nouveau mot de passe",
  passwordHint: "Au moins 10 caractères, dont une lettre et un chiffre.",
  changing: "Modification…",
  savePreferences: "Enregistrer les préférences",
  closureReason: "Pourquoi partez-vous ?",
  closureConfirm: "Je comprends que cela envoie une demande de fermeture à l’équipe Storelancer.",
  closureHint: "Rien n’est supprimé automatiquement. Nous vous contacterons d’abord pour confirmer et vous expliquer ce qu’il advient de vos boutiques, commandes et abonnement.",
  requestClosure: "Demander la fermeture du compte",
  viewRequest: "Voir votre demande",
  notifGroup_orders: "Commandes",
  notifGroup_catalog: "Catalogue",
  notifGroup_payments: "Paiements",
  notifGroup_domains: "Domaines",
  notif_orders_new: "Nouvelle commande passée",
  notif_orders_paid: "Paiement de commande confirmé",
  notif_orders_refunded: "Commande remboursée",
  notif_orders_return_requested: "Retour demandé",
  notif_inventory_low_stock: "Stock faible",
  notif_payments_failed: "Échec de paiement",
  notif_payments_dispute: "Litige ou rétrofacturation ouvert",
  notif_payments_connection_problem: "La connexion de paiement nécessite votre attention",
  notif_payments_checkout_error: "Le checkout n’a pas pu initier un paiement",
  notif_payments_mismatch: "Écart de montant de paiement",
  notif_domains_verified: "Domaine vérifié",
  notif_domains_failed: "Échec de vérification du domaine",
  profileSaved: "Profil enregistré.",
  prefsSaved: "Préférences de notification enregistrées.",
  sessionEnded: "Votre session a expiré. Veuillez vous reconnecter.",
  newPasswordsDontMatch: "Les nouveaux mots de passe ne correspondent pas.",
  passwordsDontMatch: "Les mots de passe ne correspondent pas",
  chooseStrongerPassword: "Choisissez un mot de passe plus robuste",
  passwordChanged: "Mot de passe modifié. Vous avez été déconnecté de tous les autres appareils.",
  sessionSignedOut: "Session déconnectée.",
  otherSessionsSignedOut: "Toutes les autres sessions ont été déconnectées.",
  verificationSent: "E-mail de vérification envoyé à {email}.",
  confirmClosure: "Veuillez confirmer que vous souhaitez demander la fermeture de votre compte.",
  required: "Obligatoire",
  closureSent: "Demande envoyée sous forme de ticket de support n°{number}. Notre équipe vous contactera avant toute suppression.",

  // Notifications
  notificationsDescription: "Actualités concernant vos boutiques, votre facturation et vos demandes de support.",
  markAllRead: "Tout marquer comme lu",
  unread: "Non lues",
  noUnread: "Aucune notification non lue",
  noNotifications: "Aucune notification pour l’instant",
  notificationsEmpty: "Nous vous informerons des nouvelles commandes, des problèmes de paiement, des vérifications de domaine et des réponses du support.",
  unreadSr: " (non lue)",
  markRead: "Marquer comme lue",
  notifKind_orders: "Commandes",
  notifKind_inventory: "Stock",
  notifKind_payments: "Paiements",
  notifKind_domains: "Domaines",
  notifKind_billing: "Facturation",
  notifKind_support: "Support",
  notifKind_security: "Sécurité",
  notifKind_account: "Compte",
  notifKind_team: "Équipe",
  notifKind_store: "Boutique",
  notifKind_system: "Système",
  allCaughtUp: "Vous êtes à jour.",
  unknownNotification: "Notification inconnue",

  // Billing
  billingDescription: "Votre abonnement Storelancer. Il est distinct des paiements effectués par vos clients dans vos boutiques, qui sont versés à votre propre prestataire de paiement.",
  checkoutDoneTitle: "Merci — paiement effectué",
  checkoutDoneBody: "Votre forfait est mis à jour dès que notre prestataire de facturation confirme le paiement. Cela prend généralement quelques secondes ; actualisez la page si rien n’a changé.",
  planUpdated: "Votre forfait a été mis à jour. Les informations ci-dessous reflètent ce changement.",
  checkoutCancelled: "Le paiement a été annulé. Votre forfait n’a pas changé.",
  billingUnavailableTitle: "Le paiement en ligne des forfaits payants n’est pas encore disponible",
  billingUnavailableBody: "La facturation de la plateforme n’a pas encore été configurée par l’opérateur : les forfaits payants ne peuvent donc pas être achetés ici pour le moment. Vous pouvez toujours passer à un forfait gratuit.",
  contactSupportToUpgrade: "{link} pour passer à un forfait supérieur.",
  contactSupport: "Contactez le support",
  free: "Gratuit",
  pricePerMonth: "{price} / mois",
  pricePerYear: "{price} / an",
  perMonthSuffix: " / mois",
  orPerYear: "ou {price} / an",
  billing: "Facturation",
  billedYearly: "Facturation annuelle",
  billedMonthly: "Facturation mensuelle",
  noPaymentNeeded: "Aucun paiement requis",
  assignedByPlatform: "Attribué par Storelancer (aucune carte enregistrée)",
  trialEnds: "Fin de l’essai",
  endsOn: "Se termine le",
  renewsOn: "Renouvellement le",
  movingToPlan: "Vous passerez au forfait {plan} à la fin de la période en cours.",
  subscriptionEnding: "Votre abonnement prendra fin à l’issue de la période en cours.",
  dataKept: "Vos boutiques et vos données sont conservées dans tous les cas.",
  pastDue: "Le dernier paiement de votre abonnement a échoué. Mettez à jour votre moyen de paiement depuis la dernière facture pour conserver votre forfait.",
  cancelTitle: "Annuler votre abonnement ?",
  cancelBody: "Vous conservez votre forfait actuel jusqu’à la fin de la période de facturation, puis vous passez au forfait gratuit. Rien n’est supprimé — si vous dépassez les limites du forfait gratuit, vous ne pourrez simplement plus rien ajouter.",
  cancelSubscription: "Annuler l’abonnement",
  planEnding: "Votre forfait prendra fin à l’issue de la période de facturation en cours. Vos données sont conservées.",
  usage: "Utilisation",
  storesYouOwn: "Boutiques dont vous êtes propriétaire",
  perStoreUsage: "L’utilisation propre à chaque boutique est indiquée dans ses paramètres.",
  changePlan: "Changer de forfait",
  changePlanDescription: "Passer à un forfait inférieur ne supprime jamais de boutiques, de produits ni de commandes. Si vous dépassez une limite, vous conservez l’existant mais ne pouvez plus rien ajouter tant que vous n’êtes pas repassé sous cette limite.",
  switchToTitle: "Passer au forfait {plan} ?",
  switchBodyPaid: "Votre abonnement payant prend fin à l’issue de la période de facturation en cours, puis vous passez à ce forfait. Rien n’est supprimé.",
  switchBodyFree: "Le changement s’applique immédiatement. Rien n’est supprimé, mais vous ne pourrez pas dépasser ce que ce forfait autorise.",
  switchTo: "Passer au forfait {plan}",
  startingCheckout: "Ouverture du paiement…",
  startTrial: "Démarrer l’essai de {days} jours",
  startTrial_one: "Démarrer l’essai d’un jour",
  upgradeMonthly: "Souscrire au mois",
  yearly: "À l’année",
  invoices: "Factures",
  invoicesDescription: "Factures de votre abonnement Storelancer.",
  noInvoices: "Aucune facture pour l’instant",
  noInvoicesDescription: "Vos factures apparaîtront ici après le premier paiement de votre abonnement payant.",
  colInvoice: "Facture",
  colPeriod: "Période",
  colLink: "Lien",
  invoiceSr: " la facture {number}",

  // Support
  supportDescription: "Demandez de l’aide à l’équipe Storelancer. Nous répondons ici et par e-mail.",
  yourRequests: "Vos demandes",
  noRequests: "Aucune demande de support pour l’instant",
  noRequestsDescription: "Avant de nous écrire, consultez les {link} : la réponse s’y trouve peut-être déjà.",
  setupGuides: "guides de configuration",
  colRequest: "Demande",
  colCategory: "Catégorie",
  awaitingReply: "En attente de votre réponse",
  newRequest: "Nouvelle demande",
  cat_general: "Question générale",
  cat_billing: "Forfait et facturation",
  cat_payments: "Paiements et versements",
  cat_domains: "Domaines",
  cat_technical: "Problème technique",
  cat_account: "Compte et connexion",
  fieldSubject: "Objet",
  fieldCategory: "Catégorie",
  fieldStore: "Boutique",
  notAboutStore: "Ne concerne pas une boutique en particulier",
  fieldHowCanWeHelp: "Comment pouvons-nous vous aider ?",
  ticketBodyHint: "Indiquez ce que vous attendiez et ce qui s’est passé. Ne communiquez jamais de mots de passe ni de clés API.",
  sendToSupport: "Envoyer au support",
  fieldYourReply: "Votre réponse",
  sendReply: "Envoyer la réponse",
  subjectLength: "Utilisez entre 3 et 150 caractères",
  bodyLength: "Décrivez le problème en au moins 10 caractères",
  checkFields: "Veuillez vérifier les champs signalés.",
  writeMessageFirst: "Rédigez d’abord un message.",
  replySent: "Réponse envoyée.",
  ticketMeta: "Demande de support",
  ticketSummary: "Demande n°{number} · {category} · ouverte le {date}",
  requestSent: "Votre demande a été envoyée. Nous vous répondrons ici et par e-mail à {email}.",
  conversation: "Conversation",
  supportTeam: "Support Storelancer",
  you: "Vous",
  requestClosed: "Cette demande est fermée",
  reply: "Répondre",
  replyReopens: "Répondre rouvrira la demande.",

  // Invitations
  inviteMeta: "Invitation à une boutique",
  homeLink: "Accueil Storelancer",
  inviteUnavailable: "Invitation indisponible",
  inviteUnavailableDescription: "Ce lien d’invitation est invalide, a expiré ou a déjà été utilisé. Demandez au propriétaire de la boutique de vous envoyer une nouvelle invitation.",
  goToYourDashboard: "Accéder à votre tableau de bord",
  invitedEyebrow: "Vous avez reçu une invitation",
  joinStore: "Rejoindre {store}",
  invitedAs: "Vous êtes invité à participer à la gestion de cette boutique en tant que",
  invitationFor: "Invitation pour",
  expires: "Expire le",
  signInToAcceptHelp: "Connectez-vous avec {email} pour accepter. Nouveau sur Storelancer ? Créez un compte avec cette adresse e-mail, puis rouvrez ce lien d’invitation.",
  signInToAccept: "Se connecter pour accepter",
  differentAccount: "Compte différent",
  differentAccountBody: "Vous êtes connecté en tant que {current}, mais cette invitation a été envoyée à {invited}. Déconnectez-vous puis connectez-vous avec l’adresse invitée pour accepter.",
  joining: "Connexion à la boutique…",
  acceptInvitation: "Accepter l’invitation",
  notNow: "Plus tard",
};

export default fr;
